/**
 * Conversion Funnel Utility Functions
 * 
 * Helper functions for color management, formatting, and calculations
 * related to conversion funnel analytics
 */

/**
 * Default color schemes for demographics (fallback if API doesn't provide)
 */
export const DEFAULT_COLOR_SCHEMES = {
  agegroup: {
    'Under 35': '#3498db',
    'Over 35': '#e74c3c'
  },
  gender: {
    'Male': '#3498db',
    'Female': '#e91e63',
    'Other': '#9c27b0'
  },
  ethnicity: {
    'White': '#3498db',
    'Black': '#9c27b0',
    'Hispanic': '#ff9800',
    'Asian/Pacific Islander': '#4caf50',
    'Other': '#757575'
  },
  movie_frequency_category: {
    'Frequents': '#27ae60',
    'Casual': '#3498db',
    'Home': '#f39c12',
    'Non-Movie Watchers': '#e74c3c'
  }
};

/**
 * Get color for a demographic segment
 * @param {string} demographic - Demographic type (agegroup, gender, etc.)
 * @param {string} segment - Segment name (Under 35, Male, etc.)
 * @param {Object} [apiColorSchemes] - Optional color schemes from API
 * @returns {string} - Hex color code
 */
export function getSegmentColor(demographic, segment, apiColorSchemes = null) {
  // Try API color schemes first
  if (apiColorSchemes && apiColorSchemes[demographic] && apiColorSchemes[demographic][segment]) {
    return apiColorSchemes[demographic][segment];
  }
  
  // Fall back to default color schemes
  if (DEFAULT_COLOR_SCHEMES[demographic] && DEFAULT_COLOR_SCHEMES[demographic][segment]) {
    return DEFAULT_COLOR_SCHEMES[demographic][segment];
  }
  
  // Ultimate fallback: generate a hash-based color
  return generateColorFromString(segment);
}

/**
 * Generate a consistent color from a string (for unknown segments)
 * @param {string} str - String to generate color from
 * @returns {string} - Hex color code
 */
function generateColorFromString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const h = hash % 360;
  const s = 65 + (hash % 20); // 65-85%
  const l = 45 + (hash % 15); // 45-60%
  
  return hslToHex(h, s, l);
}

/**
 * Convert HSL to Hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} - Hex color code
 */
function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Format conversion rate for display
 * @param {number} rate - Conversion rate (0-100)
 * @param {number} [decimals=1] - Number of decimal places
 * @returns {string} - Formatted rate (e.g., "75.5%")
 */
export function formatConversionRate(rate, decimals = 1) {
  if (rate === null || rate === undefined || isNaN(rate)) {
    return '0.0%';
  }
  return `${rate.toFixed(decimals)}%`;
}

/**
 * Format large numbers with commas
 * @param {number} num - Number to format
 * @returns {string} - Formatted number (e.g., "1,234")
 */
export function formatNumber(num) {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  return num.toLocaleString('en-US');
}

/**
 * Get performance level classification for conversion rate
 * @param {number} rate - Conversion rate (0-100)
 * @returns {{level: string, color: string, label: string}} - Performance classification
 */
export function getPerformanceLevel(rate) {
  if (rate >= 75) {
    return {
      level: 'excellent',
      color: '#4caf50',
      label: 'Excellent'
    };
  } else if (rate >= 50) {
    return {
      level: 'good',
      color: '#8bc34a',
      label: 'Good'
    };
  } else if (rate >= 25) {
    return {
      level: 'moderate',
      color: '#ff9800',
      label: 'Moderate'
    };
  } else {
    return {
      level: 'poor',
      color: '#f44336',
      label: 'Poor'
    };
  }
}

/**
 * Calculate drop-off percentage between two stages
 * @param {number} currentStage - Current stage count
 * @param {number} previousStage - Previous stage count
 * @returns {number} - Drop-off percentage
 */
export function calculateDropoffPercentage(currentStage, previousStage) {
  if (previousStage === 0) return 0;
  const dropped = previousStage - currentStage;
  return (dropped / previousStage) * 100;
}

/**
 * Calculate conversion rate between two stages
 * @param {number} currentStage - Current stage count
 * @param {number} previousStage - Previous stage count
 * @returns {number} - Conversion rate percentage
 */
export function calculateConversionRate(currentStage, previousStage) {
  if (previousStage === 0) return 0;
  return (currentStage / previousStage) * 100;
}

/**
 * Get stage name by number
 * @param {number} stageNumber - Stage number (1-5)
 * @returns {string} - Stage name
 */
export function getStageName(stageNumber) {
  const stages = {
    1: 'Aware',
    2: 'Interested',
    3: 'Willing to Pay',
    4: 'Theater Intent',
    5: 'Opening Weekend'
  };
  return stages[stageNumber] || `Stage ${stageNumber}`;
}

/**
 * Get stage short name for compact display
 * @param {number} stageNumber - Stage number (1-5)
 * @returns {string} - Short stage name
 */
export function getStageShortName(stageNumber) {
  const stages = {
    1: 'Aware',
    2: 'Interest',
    3: 'Pay',
    4: 'Theater',
    5: 'Opening'
  };
  return stages[stageNumber] || `S${stageNumber}`;
}

/**
 * Build Sankey nodes from demographic breakdown
 * @param {Object} demographicBreakdown - Demographic breakdown data
 * @param {Object} [colorSchemes] - Color schemes from API
 * @returns {Array} - Array of Sankey nodes
 */
export function buildSankeyNodes(demographicBreakdown, colorSchemes = null) {
  if (!demographicBreakdown || !demographicBreakdown.stages) {
    return [];
  }
  
  const nodes = [];
  const demographicType = demographicBreakdown.demographic;
  
  demographicBreakdown.stages.forEach(stage => {
    Object.keys(stage.segments).forEach(segment => {
      nodes.push({
        name: `${getStageName(stage.stage)} - ${segment}`,
        stageName: getStageName(stage.stage),
        segment: segment,
        stage: stage.stage,
        category: segment,
        color: getSegmentColor(demographicType, segment, colorSchemes)
      });
    });
  });
  
  return nodes;
}

/**
 * Build Sankey links from demographic breakdown
 * @param {Object} demographicBreakdown - Demographic breakdown data
 * @param {Array} nodes - Array of Sankey nodes
 * @returns {Array} - Array of Sankey links
 */
export function buildSankeyLinks(demographicBreakdown, nodes) {
  if (!demographicBreakdown || !demographicBreakdown.stages || !nodes) {
    return [];
  }
  
  const links = [];
  
  // For each stage (except the first), create links from previous stage
  for (let i = 1; i < demographicBreakdown.stages.length; i++) {
    const currentStage = demographicBreakdown.stages[i];
    const previousStage = demographicBreakdown.stages[i - 1];
    
    // For each segment in current stage, find corresponding previous segment
    Object.entries(currentStage.segments).forEach(([segment, metrics]) => {
      const sourceNode = nodes.find(n => 
        n.stage === previousStage.stage && n.segment === segment
      );
      const targetNode = nodes.find(n => 
        n.stage === currentStage.stage && n.segment === segment
      );
      
      if (sourceNode && targetNode && metrics.count > 0) {
        links.push({
          source: nodes.indexOf(sourceNode),
          target: nodes.indexOf(targetNode),
          value: metrics.count,
          sourceSegment: segment,
          targetSegment: segment
        });
      }
    });
  }
  
  return links;
}

/**
 * Find best and worst performing segments
 * @param {Object} demographicBreakdown - Demographic breakdown data
 * @param {number} targetStage - Stage to analyze (2-5)
 * @returns {{best: Object, worst: Object}} - Best and worst performers
 */
export function findBestWorstPerformers(demographicBreakdown, targetStage = 2) {
  if (!demographicBreakdown || !demographicBreakdown.stages) {
    return { best: null, worst: null };
  }
  
  const stage = demographicBreakdown.stages.find(s => s.stage === targetStage);
  if (!stage) {
    return { best: null, worst: null };
  }
  
  const segments = Object.entries(stage.segments)
    .filter(([_, metrics]) => metrics.conversion_from_previous_stage !== undefined)
    .map(([segment, metrics]) => ({
      segment,
      conversionRate: metrics.conversion_from_previous_stage,
      count: metrics.count
    }))
    .sort((a, b) => b.conversionRate - a.conversionRate);
  
  return {
    best: segments[0] || null,
    worst: segments[segments.length - 1] || null
  };
}

/**
 * Calculate overall funnel efficiency
 * @param {Object} funnelOverview - Funnel overview data
 * @returns {number} - Overall efficiency percentage (0-100)
 */
export function calculateFunnelEfficiency(funnelOverview) {
  if (!funnelOverview || !funnelOverview.stage_1_aware || !funnelOverview.stage_5_opening_weekend) {
    return 0;
  }
  
  const awareCount = funnelOverview.stage_1_aware.count;
  const openingCount = funnelOverview.stage_5_opening_weekend.count;
  
  if (awareCount === 0) return 0;
  
  return (openingCount / awareCount) * 100;
}

/**
 * Get recommended action based on conversion rate
 * @param {number} rate - Conversion rate
 * @param {string} stageName - Name of the stage
 * @returns {string} - Recommended action
 */
export function getRecommendedAction(rate, stageName) {
  if (rate >= 75) {
    return `${stageName} conversion is excellent. Maintain current strategy.`;
  } else if (rate >= 50) {
    return `${stageName} conversion is good. Look for optimization opportunities.`;
  } else if (rate >= 25) {
    return `${stageName} conversion is moderate. Consider targeted campaigns.`;
  } else {
    return `${stageName} conversion is low. Immediate attention needed.`;
  }
}

/**
 * Format stage metrics for display
 * @param {Object} stage - Stage data
 * @returns {Object} - Formatted stage data
 */
export function formatStageMetrics(stage) {
  return {
    count: formatNumber(stage.count),
    pctOfResponses: formatConversionRate(stage.pct_of_responses),
    pctOfAware: formatConversionRate(stage.pct_of_aware),
    conversionRate: stage.conversion_rate ? formatConversionRate(stage.conversion_rate) : null
  };
}

const conversionFunnelUtils = {
  DEFAULT_COLOR_SCHEMES,
  getSegmentColor,
  formatConversionRate,
  formatNumber,
  getPerformanceLevel,
  calculateDropoffPercentage,
  calculateConversionRate,
  getStageName,
  getStageShortName,
  buildSankeyNodes,
  buildSankeyLinks,
  findBestWorstPerformers,
  calculateFunnelEfficiency,
  getRecommendedAction,
  formatStageMetrics
};

export default conversionFunnelUtils;

